import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faTrashArrowUp, faRotateLeft } from '@fortawesome/free-solid-svg-icons';
import { customerService } from '@/services/customer.service';
import { SkeletonPage } from '@/components/skeletons';
import { Button } from '@/components/ui/button';
import { toast } from '@/lib/swal';
import { ROUTES } from '@/routes/routes';

const selectClass =
  'w-full max-w-sm rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary';

function reasonBadge(reason: string | null) {
  if (!reason) return null;
  return (
    <span className="ml-2 rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
      {reason === 'CASCADE' ? 'via customer' : 'direct'}
    </span>
  );
}

export function CustomerRecoveryBinPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [selectedCustomer, setSelectedCustomer] = useState('');

  const {
    data: deletedCustomers,
    isLoading,
    isError: deletedFailed,
    refetch: refetchDeleted,
  } = useQuery({
    queryKey: ['deleted-customers'],
    queryFn: () => customerService.listDeleted({ pageSize: 100 }),
  });
  // pageSize must stay within the endpoint's cap (`customer-list-query.dto.ts`:
  // `@Max(100)`). 200 made the request 400, so the customer picker below was
  // permanently empty and no location or contact could ever be inspected —
  // with nothing on screen to say the page had failed rather than being bare.
  const { data: activeCustomers, isError: activeFailed } = useQuery({
    queryKey: ['active-customers-all'],
    queryFn: () => customerService.list({ pageSize: 100 }),
  });

  // A location/contact deleted via CASCADE (its parent customer was deleted)
  // lives under a customer that no longer appears in the active list. To make
  // those records reachable in the Recovery Bin, the inspector must be able to
  // select deleted customers too. Individual restore is disabled for a deleted
  // customer's records — those are recovered by restoring the customer itself
  // (cascade-restore), which avoids orphaning a location under a deleted parent.
  const inspectableCustomers = [
    ...(activeCustomers?.items ?? []).map((c) => ({ ...c, isDeletedParent: false })),
    ...(deletedCustomers?.items ?? []).map((c) => ({ ...c, isDeletedParent: true })),
  ];
  const selectedIsDeleted =
    inspectableCustomers.find((c) => c.id === selectedCustomer)?.isDeletedParent ?? false;
  const { data: deletedLocations } = useQuery({
    queryKey: ['deleted-locations', selectedCustomer],
    queryFn: () => customerService.listDeletedLocations(selectedCustomer),
    enabled: !!selectedCustomer,
  });
  const { data: deletedContacts } = useQuery({
    queryKey: ['deleted-contacts', selectedCustomer],
    queryFn: () => customerService.listDeletedContacts(selectedCustomer),
    enabled: !!selectedCustomer,
  });

  const restoreCustomer = useMutation({
    mutationFn: (id: string) => customerService.restore(id),
    onSuccess: () => { toast.success('Customer restored'); void qc.invalidateQueries({ queryKey: ['deleted-customers'] }); void qc.invalidateQueries({ queryKey: ['customers'] }); },
    onError: () => toast.error('Failed to restore customer'),
  });
  const restoreLocation = useMutation({
    mutationFn: (id: string) => customerService.restoreLocation(selectedCustomer, id),
    onSuccess: () => { toast.success('Location restored'); void qc.invalidateQueries({ queryKey: ['deleted-locations', selectedCustomer] }); },
    onError: () => toast.error('Failed to restore location'),
  });
  const restoreContact = useMutation({
    mutationFn: (id: string) => customerService.restoreContact(selectedCustomer, id),
    onSuccess: () => { toast.success('Contact restored'); void qc.invalidateQueries({ queryKey: ['deleted-contacts', selectedCustomer] }); },
    onError: () => toast.error('Failed to restore contact'),
  });

  if (isLoading) return <SkeletonPage />;

  return (
    <div className="space-y-6">
      <button onClick={() => navigate(ROUTES.CUSTOMERS)} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
        <FontAwesomeIcon icon={faArrowLeft} className="h-3 w-3" /> Back to Customers
      </button>

      <div>
        <h1 className="flex items-center gap-2 text-xl font-semibold text-foreground">
          <FontAwesomeIcon icon={faTrashArrowUp} className="h-4 w-4 text-primary" /> Recovery Bin
        </h1>
        <p className="mt-0.5 text-sm text-muted-foreground">Restore deleted records. Permanent delete is disabled.</p>
      </div>

      {/* Deleted Customers */}
      <section className="space-y-2">
        <h2 className="text-sm font-semibold text-foreground">Deleted Customers ({deletedCustomers?.total ?? 0})</h2>
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full min-w-[600px] text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/40 text-left">
                <th className="px-4 py-3 font-medium text-foreground">Code</th>
                <th className="px-4 py-3 font-medium text-foreground">Name</th>
                <th className="px-4 py-3 font-medium text-foreground">Deleted At</th>
                <th className="px-4 py-3 text-right font-medium text-foreground">Action</th>
              </tr>
            </thead>
            <tbody>
              {deletedCustomers && deletedCustomers.items.length > 0 ? deletedCustomers.items.map((c) => (
                <tr key={c.id} className="border-b border-border last:border-0">
                  <td className="px-4 py-3 font-mono text-xs">{c.customerCode ?? '—'}</td>
                  <td className="px-4 py-3 font-medium text-foreground">{c.name}</td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">{c.deletedAt ? new Date(c.deletedAt).toLocaleString() : '—'}</td>
                  <td className="px-4 py-3 text-right">
                    <Button size="sm" variant="secondary" onClick={() => restoreCustomer.mutate(c.id)} disabled={restoreCustomer.isPending}>
                      <FontAwesomeIcon icon={faRotateLeft} className="mr-1.5 h-3 w-3" /> Restore
                    </Button>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center">
                    {deletedFailed ? (
                      <span className="flex flex-col items-center gap-2 text-sm text-destructive">
                        Could not load deleted customers.
                        <Button size="sm" variant="secondary" onClick={() => void refetchDeleted()}>
                          <FontAwesomeIcon icon={faRotateLeft} className="mr-1.5 h-3 w-3" /> Try again
                        </Button>
                      </span>
                    ) : (
                      <span className="text-muted-foreground">No deleted customers.</span>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Deleted Locations & Contacts (per customer) */}
      <section className="space-y-3">
        <h2 className="text-sm font-semibold text-foreground">Deleted Locations &amp; Contacts</h2>
        <select className={selectClass} value={selectedCustomer} onChange={(e) => setSelectedCustomer(e.target.value)}>
          <option value="">Select a customer to inspect…</option>
          {inspectableCustomers.map((c) => (
            <option key={c.id} value={c.id}>
              {c.customerCode ? `${c.customerCode} · ` : ''}{c.name}{c.isDeletedParent ? ' (deleted)' : ''}
            </option>
          ))}
        </select>

        {activeFailed && (
          <p className="text-xs text-destructive">
            Could not load the customer list — active customers are missing from the selector above.
            Reload the page to try again.
          </p>
        )}

        {selectedCustomer && selectedIsDeleted && (
          <p className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-400">
            This customer is in the Recovery Bin. Its locations and contacts were removed together with it — restore the customer above to recover them all at once.
          </p>
        )}

        {selectedCustomer && (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="rounded-lg border border-border">
              <div className="border-b border-border bg-muted/40 px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Deleted Locations</div>
              <ul className="divide-y divide-border">
                {deletedLocations && deletedLocations.length > 0 ? deletedLocations.map((l) => (
                  <li key={l.id} className="flex items-center justify-between px-4 py-2.5 text-sm">
                    <span className="text-foreground">{l.name}{reasonBadge(l.deletedReason)}</span>
                    <Button size="sm" variant="ghost" onClick={() => restoreLocation.mutate(l.id)} disabled={restoreLocation.isPending || selectedIsDeleted} title={selectedIsDeleted ? 'Restore the customer to recover this location' : undefined}>
                      <FontAwesomeIcon icon={faRotateLeft} className="mr-1.5 h-3 w-3" /> Restore
                    </Button>
                  </li>
                )) : <li className="px-4 py-6 text-center text-xs text-muted-foreground">None.</li>}
              </ul>
            </div>
            <div className="rounded-lg border border-border">
              <div className="border-b border-border bg-muted/40 px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Deleted Contacts</div>
              <ul className="divide-y divide-border">
                {deletedContacts && deletedContacts.length > 0 ? deletedContacts.map((c) => (
                  <li key={c.id} className="flex items-center justify-between px-4 py-2.5 text-sm">
                    <span className="text-foreground">{c.firstName} {c.lastName}{reasonBadge(c.deletedReason)}</span>
                    <Button size="sm" variant="ghost" onClick={() => restoreContact.mutate(c.id)} disabled={restoreContact.isPending || selectedIsDeleted} title={selectedIsDeleted ? 'Restore the customer to recover this contact' : undefined}>
                      <FontAwesomeIcon icon={faRotateLeft} className="mr-1.5 h-3 w-3" /> Restore
                    </Button>
                  </li>
                )) : <li className="px-4 py-6 text-center text-xs text-muted-foreground">None.</li>}
              </ul>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
